import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useLayoutMetrics
 * Measures header and footer heights using ResizeObserver and writes CSS variables
 * on the provided root element for stable, resolution/zoom-independent layout.
 *
 * Exposed CSS vars on root:
 *  - --rms-header-height
 *  - --rms-footer-height
 *  - --rms-viewport-height  (calc(innerHeight - header - footer))
 *  - --rms-question-top-gap (configurable)
 */
export default function useLayoutMetrics(options = {}) {
  const {
    defaultQuestionTopGapPx = 36,
    freezeHeaderAfterInit = true,
  } = options;

  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [questionTopGap, setQuestionTopGap] = useState(defaultQuestionTopGapPx);
  const headerFrozenRef = useRef(false);

  const writeVars = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const h = Number.isFinite(headerHeight) ? headerHeight : 0;
    const f = Number.isFinite(footerHeight) ? footerHeight : 0;
    let vh = 0;
    if (typeof window !== 'undefined') {
      const vv = window.visualViewport;
      vh = vv && typeof vv.height === 'number' ? vv.height : window.innerHeight;
    }
    const viewportHeight = Math.max(vh - h - f, 0);
    try {
      root.style.setProperty('--rms-header-height', `${h}px`);
      root.style.setProperty('--rms-footer-height', `${f}px`);
      root.style.setProperty('--rms-viewport-height', `${viewportHeight}px`);
      root.style.setProperty('--rms-question-top-gap', `${questionTopGap}px`);
    } catch (_) {}
  }, [headerHeight, footerHeight, questionTopGap]);

  useEffect(() => {
    const RO = (typeof window !== 'undefined' && window.ResizeObserver) ? window.ResizeObserver : null;
    const hEl = headerRef.current;
    const fEl = footerRef.current;
    let hObs = null;
    let fObs = null;

    const measureFallback = () => {
      if (hEl && !(freezeHeaderAfterInit && headerFrozenRef.current)) {
        setHeaderHeight(hEl.offsetHeight || 0);
      }
      if (fEl) setFooterHeight(fEl.offsetHeight || 0);
    };

    if (RO && hEl) {
      hObs = new RO((entries) => {
        for (const entry of entries) {
          if (freezeHeaderAfterInit && headerFrozenRef.current) continue;
          const rect = entry.contentRect || {};
          const measured = Math.round(rect.height || hEl.offsetHeight || 0);
          setHeaderHeight(measured);
        }
      });
      hObs.observe(hEl);
    } else {
      measureFallback();
    }
    if (RO && fEl) {
      fObs = new RO((entries) => {
        for (const entry of entries) {
          const rect = entry.contentRect || {};
          setFooterHeight(Math.round(rect.height || fEl.offsetHeight || 0));
        }
      });
      fObs.observe(fEl);
    } else {
      measureFallback();
    }

    const onResize = () => writeVars();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    // Track visual viewport changes (keyboard show/hide, pinch-zoom)
    if (typeof window !== 'undefined' && window.visualViewport) {
      try {
        window.visualViewport.addEventListener('resize', onResize);
        window.visualViewport.addEventListener('scroll', onResize);
      } catch (_) {}
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      try { if (hObs) hObs.disconnect(); } catch (_) {}
      try { if (fObs) fObs.disconnect(); } catch (_) {}
      try {
        if (typeof window !== 'undefined' && window.visualViewport) {
          window.visualViewport.removeEventListener('resize', onResize);
          window.visualViewport.removeEventListener('scroll', onResize);
        }
      } catch (_) {}
    };
  }, [writeVars, freezeHeaderAfterInit]);

  useEffect(() => {
    writeVars();
  }, [writeVars]);

  // After first successful headerHeight write, optionally freeze the header measurement
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (!freezeHeaderAfterInit) return;
    if (headerHeight > 0 && !headerFrozenRef.current) {
      // Mark frozen so subsequent header resizes (e.g., title wrap) won't change layout vars
      headerFrozenRef.current = true;
      try {
        root.setAttribute('data-header-frozen', 'true');
      } catch (_) {}
    }
  }, [headerHeight, freezeHeaderAfterInit]);

  const setGap = useCallback((px) => {
    if (!Number.isFinite(px)) return;
    setQuestionTopGap(Math.max(0, Math.round(px)));
  }, []);

  return useMemo(() => ({
    rootRef,
    headerRef,
    footerRef,
    headerHeight,
    footerHeight,
    questionTopGap,
    setQuestionTopGap: setGap,
  }), [headerHeight, footerHeight, questionTopGap, setGap]);
}


